import { DeleteFolderRequestParams, DeleteFolderResponseParams, Folder } from "../types";
import { useState } from "react";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";
import { Button, Modal, ModalActions, ModalContent, ModalTitle, Typography } from "@kaltura/ds-react-components";
import { baseUrl, postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { useFoldersStore } from "../foldersStore";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

export interface DeleteFolderModalProps {
    /** folder object to delete */
    folder: Folder;
    /** boolean flag to indicate the modal is currently open */
    isOpen: boolean;
    /** callback to execute when clicking cancel */
    onClose: () => void;
    /** callback to execute after successful delete folder request */
    onSubmitEnd?: () => void;
}

const baseClassName = "kms-ds-folders-delete-modal";

/**
 * delete folder modal
 * handles deleting a folder
 */
export function DeleteFolderModal({ folder, isOpen, onClose, onSubmitEnd }: DeleteFolderModalProps) {
    const [isProcessing, setIsProcessing] = useState(false);

    const listFolders = useFoldersStore((state) => state.listFolders);

    const { showToast } = useToastsContext();
    const sendButtonAnalytics = useButtonAnalytics();

    const onSubmit = async (data: DeleteFolderRequestParams) => {
        sendButtonAnalytics("Delete folder - confirm", ButtonClickAnalyticsType.DELETE);
        setIsProcessing(true);
        try {
            const result: DeleteFolderResponseParams = await postKmsData(`${baseUrl}/folders/folders/delete`, data);
            setIsProcessing(false);

            showToast({
                severity: result.success ? "success" : "error",
                message: result.message,
            });

            if (result.success) {
                onSubmitEnd?.();
                // refresh store folders
                try {
                    await listFolders();
                }
                catch (e) {
                    showToast({
                        severity: "error",
                        message: translate("Failed to list updated folders"),
                    });
                }
            }
        }
        catch (e) {
            setIsProcessing(false);
            showToast({
                severity: "error",
                message: translate("Failed to delete %1 folder", [folder.name]),
            });
        }
    };

    const handleClose = () => {
        sendButtonAnalytics("Delete folder - cancel", ButtonClickAnalyticsType.CLOSE);
        onClose();
    };

    return (
        <Modal
            classes={{
                root: `${baseClassName}-root`,
                paper: `${baseClassName}-paper`,
            }}
            open={isOpen}
            fullWidth={true}
        >
            <ModalTitle className={`${baseClassName}-title`}>
                {translate("Delete this folder and its subfolders?")}
            </ModalTitle>
            <ModalContent className={`${baseClassName}-content`}>
                <Typography variant={"body1"} component={"p"} align={"center"}>
                    {translate("Don't worry - videos and other media won't be deleted.")}
                    <br />
                    {translate("This action can’t be undone.")}
                </Typography>
            </ModalContent>
            <ModalActions className={`${baseClassName}-actions`}>
                <Button
                    onClick={handleClose}
                    variant={"borderless"}
                    color={"secondary"}
                    className={`${baseClassName}-actions-cancel`}
                >
                    {translate("Cancel")}
                </Button>
                <Button
                    loading={isProcessing}
                    disabled={isProcessing}
                    className={`${baseClassName}-actions-submit`}
                    color={"danger"}
                    onClick={() => onSubmit({ id: folder.id })}
                >
                    {translate("Delete")}
                </Button>
            </ModalActions>
        </Modal>
    );
}

export default DeleteFolderModal;
